import React, { useEffect, useState, useCallback, useRef } from "react";
import { PieChart, BarChart, LineChart } from "@mui/x-charts";
import {
  FaChevronDown,
  FaChevronUp,
  FaChartArea,
  FaExpand,
  FaCompress,
} from "react-icons/fa";
import { MdOutlineFileDownload } from "react-icons/md";
import { format } from "date-fns";

import {
  apiRequestBodyBuilder,
  getCurrencySymbolFromISO,
} from "../config/tableHelper";
import MetricsModal from "../Tools/ModifyChartColumns";
import { downloadChart } from "../Components/DownloadFuncs";

const API_URL = "https://api.confidanto.com/ads-overview-data/chart";

// Classic variations of Dark Blue, Indigo, Cyan, and Teal
export const dynamicPalette = [
  "#1565C0", // Dark Blue (Blue 800)
  "#3F51B5", // Indigo 500
  "#00ACC1", // Cyan 600
  "#26A69A", // Teal 400
  "#2196F3", // Blue 500
  "#64B5F6", // Blue 300
  "#9FA8DA", // Indigo 200
  "#80DEEA", // Cyan 200
];

const METRIC_COLORS = {
  clicks: { border: "#1565C0" },
  costs: { border: "#00ACC1" },
  cost: { border: "#00ACC1" },
  average_cpc: { border: "#3F51B5" },
  ctr: { border: "#26A69A" },
  conversions: { border: "#2196F3" },
  impressions: { border: "#64B5F6" },
};

export const ALL_METRICS = [
  {
    id: "1",
    title: "Clicks",
    key: "clicks",
    type: "number",
    category: "Performance",
  },
  {
    id: "2",
    title: "Impr.",
    key: "impressions",
    type: "number",
    category: "Performance",
  },
  {
    id: "3",
    title: "Costs (₹)",
    key: "costs",
    type: "currency",
    category: "Performance",
  },
  {
    id: "4",
    title: "CTR (%)",
    key: "ctr",
    type: "float",
    category: "Performance",
  },
  {
    id: "5",
    title: "Average CPC",
    key: "average_cpc",
    type: "currency",
    category: "Performance",
  },
  {
    id: "6",
    title: "Conversions",
    key: "conversions",
    type: "number",
    category: "Conversions",
  },
  {
    id: "7",
    title: "Conv Rate",
    key: "conversion_rate",
    type: "float",
    category: "Conversions",
  },
  {
    id: "8",
    title: "Cost/Conv",
    key: "cost_per_conversion",
    type: "currency",
    category: "Conversions",
  },
  {
    id: "9",
    title: "CPL",
    key: "CPL",
    type: "currency",
    category: "Conversions",
  },
  {
    id: "10",
    title: "Total Budget",
    key: "total_budget",
    type: "currency",
    category: "Budget",
  },
  {
    id: "11",
    title: "Monthly Budget",
    key: "monthly_budget",
    type: "currency",
    category: "Budget",
  },
  {
    id: "12",
    title: "Remaining Monthly Budget",
    key: "remaining_monthly_budget",
    type: "currency",
    category: "Budget",
  },
  {
    id: "13",
    title: "Planned Daily Spend",
    key: "planned_daily_spend",
    type: "currency",
    category: "Budget",
  },
  {
    id: "14",
    title: "Revised Daily Spend",
    key: "revised_daily_spend",
    type: "currency",
    category: "Budget",
  },
  {
    id: "15",
    title: "Agency Fees",
    key: "agency_fees",
    type: "currency",
    category: "Budget",
  },
];

const getMetricColor = (metricKey, index = 0) => {
  return (
    METRIC_COLORS[metricKey]?.border ||
    dynamicPalette[index % dynamicPalette.length]
  );
};

const formatDateRange = (startDateStr, endDateStr) => {
  if (!startDateStr || !endDateStr) return "";
  try {
    const s = new Date(startDateStr);
    const e = new Date(endDateStr);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) {
      return `${startDateStr} – ${endDateStr}`;
    }
    const sMonth = s.toLocaleString("default", { month: "short" });
    const eMonth = e.toLocaleString("default", { month: "short" });
    const sDay = s.getDate();
    const eDay = e.getDate();
    const sYear = s.getFullYear();
    const eYear = e.getFullYear();

    if (sYear === eYear) {
      if (sMonth === eMonth) {
        return `${sMonth} ${sDay} – ${eDay}, ${sYear}`;
      }
      return `${sMonth} ${sDay} – ${eMonth} ${eDay}, ${sYear}`;
    }
    return `${sMonth} ${sDay}, ${sYear} – ${eMonth} ${eDay}, ${sYear}`;
  } catch {
    return `${startDateStr} – ${endDateStr}`;
  }
};

const PaidAdsChart = ({
  metric: initialMetric = "clicks",
  previousDates,
  updateTableMetric,
  arrayIndex,
  PageType,
}) => {
  const [loading, setLoading] = useState(true);
  const [chartDataMap, setChartDataMap] = useState({});
  const [error, setError] = useState(null);

  const [selectedMetrics, setSelectedMetrics] = useState([initialMetric]);
  const [chartType, setChartType] = useState("line");

  const [isExpanded, setIsExpanded] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Adjust menu & Modal state
  const [isAdjustOpen, setIsAdjustOpen] = useState(false);
  const adjustMenuRef = useRef(null);
  const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);

  const [hiddenProjects, setHiddenProjects] = useState([]);
  const [hiddenMetrics, setHiddenMetrics] = useState([]);

  // Chart Download states & refs
  const [isChartDownloading, setIsChartDownloading] = useState(false);
  const [showChartDownloadMenu, setShowChartDownloadMenu] = useState(false);
  const chartDownloadRef = useRef(null);
  const chartWrapperRef = useRef(null);

  const selectionLimit = chartType === "pie" ? 1 : 3;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        adjustMenuRef.current &&
        !adjustMenuRef.current.contains(event.target)
      ) {
        setIsAdjustOpen(false);
      }
      if (
        chartDownloadRef.current &&
        !chartDownloadRef.current.contains(event.target)
      ) {
        setShowChartDownloadMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchChartData = useCallback(async () => {
    const startDate = previousDates?.curr_start_date;
    const endDate = previousDates?.curr_end_date;
    const PrevstartDate = previousDates?.prev_start_date;
    const PrevendDate = previousDates?.prev_end_date;

    if (!startDate || !endDate) {
      setError("Invalid date range provided.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const metricsToFetch =
        chartType === "pie" ? [selectedMetrics[0]] : selectedMetrics;

      const fetchPromises = metricsToFetch.map(async (metricKey) => {
        let RequestBody = apiRequestBodyBuilder(null, {
          metric: metricKey,
          start_date: startDate,
          end_date: endDate,
          reporting_type: PageType,
        });

        if (PrevstartDate && PrevendDate) {
          RequestBody.previous_start_date = PrevstartDate;
          RequestBody.previous_end_date = PrevendDate;
        }

        const response = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(RequestBody),
        });

        if (!response.ok) throw new Error(`API error: ${response.statusText}`);

        const result = await response.json();
        const apiData = Array.isArray(result) ? result : result.data || [];

        const formattedData = apiData
          .filter(
            (item) => item.project_name && !item.project_name.includes("TOTAL"),
          )
          .map((item, index) => {
            const metricData = item[metricKey];
            let currentVal = 0;
            let previousVal = null;

            if (metricData !== undefined && metricData !== null) {
              if (typeof metricData === "object" && "current" in metricData) {
                currentVal = metricData.current;
                if ("previous" in metricData) previousVal = metricData.previous;
              } else if (
                typeof metricData === "string" &&
                metricData.includes("%")
              ) {
                currentVal = parseFloat(metricData.replace("%", ""));
              } else {
                currentVal = metricData;
              }
            }

            return {
              id: index,
              label: item.project_name || "Unknown Project",
              currentValue: Number(currentVal) || 0,
              previousValue:
                previousVal !== null ? Number(previousVal) || 0 : null,
              currency: item.currency,
            };
          });

        return { metricKey, data: formattedData };
      });

      const results = await Promise.all(fetchPromises);
      const newDataMap = {};
      results.forEach((res) => {
        newDataMap[res.metricKey] = res.data;
      });

      setChartDataMap(newDataMap);
    } catch (err) {
      setError("Failed to fetch chart data.");
      setChartDataMap({});
    } finally {
      setLoading(false);
    }
  }, [selectedMetrics, chartType, previousDates, PageType]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  const handleChartTypeChange = (e) => {
    const newChartType = e.target.value;
    setChartType(newChartType);
    if (newChartType === "pie" && selectedMetrics.length > 1) {
      setSelectedMetrics([selectedMetrics[0]]);
    }
  };

  const handleApplyMetrics = (newMetrics) => {
    const oldPrimary = selectedMetrics[0];
    setSelectedMetrics(newMetrics);
    setHiddenMetrics([]);
    setIsMetricsModalOpen(false);
    if (updateTableMetric)
      updateTableMetric(arrayIndex, oldPrimary, newMetrics[0]);
  };

  const toggleFullScreen = () => {
    if (!isFullScreen && !isExpanded) setIsExpanded(true);
    setIsFullScreen(!isFullScreen);
  };

  const toggleProjectVisibility = (projectId) => {
    setHiddenProjects((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId],
    );
  };

  const handleChartDownload = async (type) => {
    setShowChartDownloadMenu(false);
    setIsChartDownloading(true);
    try {
      const filename = `Paid_Ads_Chart_${format(new Date(), "yyyy-MM-dd")}`;
      await downloadChart(chartWrapperRef.current, type, filename);
    } catch (err) {
      console.error(`Failed to generate Chart ${type.toUpperCase()}:`, err);
    } finally {
      setIsChartDownloading(false);
    }
  };

  const formatValue = (value, currencyCode, metricKey) => {
    if (value === null || value === undefined) return "";
    const column = ALL_METRICS.find((item) => item.key === metricKey);
    let finalValue = Number(value).toLocaleString();
    if (!column) return finalValue;

    let symbol = "";
    if (column.type === "currency") {
      symbol =
        currencyCode && getCurrencySymbolFromISO(currencyCode)
          ? getCurrencySymbolFromISO(currencyCode)
          : "₹";
    }

    switch (column.type) {
      case "float":
        return `${finalValue}%`;
      case "currency":
        return `${symbol}${finalValue}`;
      default:
        return finalValue;
    }
  };

  const formatYAxisValue = (value) => {
    if (value === null || value === undefined) return "";
    const baseMetricKey = selectedMetrics[0];
    const column = ALL_METRICS.find((item) => item.key === baseMetricKey);
    const compactVal = Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);

    let symbol = "";
    if (column?.type === "currency") {
      const defaultCurrency = chartDataMap[baseMetricKey]?.[0]?.currency;
      symbol =
        defaultCurrency && getCurrencySymbolFromISO(defaultCurrency)
          ? getCurrencySymbolFromISO(defaultCurrency)
          : "₹";
    }

    switch (column?.type) {
      case "float":
        return `${compactVal}%`;
      case "currency":
        return `${symbol}${compactVal}`;
      default:
        return compactVal;
    }
  };

  const safelyFormatPieValue = (v, context) => {
    const baseMetricKey = selectedMetrics[0];
    const baseData = chartDataMap[baseMetricKey] || [];
    const isObj = typeof v === "object" && v !== null;
    const val = isObj ? v.value : v;
    let currency = baseData[0]?.currency;

    if (isObj && v.currency) {
      currency = v.currency;
    } else if (
      context?.dataIndex !== undefined &&
      baseData[context.dataIndex]
    ) {
      currency = baseData[context.dataIndex].currency;
    }

    return formatValue(val, currency, baseMetricKey);
  };

  const renderChart = () => {
    let hasMeaningfulCurrent = false;
    let hasMeaningfulPrevious = false;

    selectedMetrics.forEach((m) => {
      const data = chartDataMap[m] || [];
      if (data.reduce((sum, item) => sum + item.currentValue, 0) > 0)
        hasMeaningfulCurrent = true;
      if (data.reduce((sum, item) => sum + (item.previousValue || 0), 0) > 0)
        hasMeaningfulPrevious = true;
    });

    if (!hasMeaningfulCurrent && !hasMeaningfulPrevious) {
      return (
        <div className="flex flex-col items-center justify-center py-6 text-center w-full h-full min-h-[250px]">
          <p className="text-slate-400 text-xs font-medium italic">
            No data available for the selected metric(s).
          </p>
        </div>
      );
    }

    const dynamicHeightLineBar = isFullScreen ? 500 : 260;

    switch (chartType) {
      case "pie": {
        const metricKey = selectedMetrics[0];
        const pieData = chartDataMap[metricKey] || [];
        const chartSeriesData = pieData.map((d, index) => ({
          ...d,
          color: dynamicPalette[index % dynamicPalette.length],
        }));
        const visibleData = chartSeriesData.filter(
          (d) => !hiddenProjects.includes(d.id),
        );
        const hasPrevious = chartSeriesData.some(
          (d) => d.previousValue !== null && d.previousValue !== 0,
        );

        const hasMeaningfulCurr = visibleData.some((d) => d.currentValue > 0);
        const hasMeaningfulPrev = visibleData.some(
          (d) => (d.previousValue || 0) > 0,
        );

        const totalCurrent = visibleData.reduce(
          (sum, d) => sum + (d.currentValue || 0),
          0,
        );
        const totalPrevious = visibleData.reduce(
          (sum, d) => sum + (d.previousValue || 0),
          0,
        );

        const defaultCurrency = pieData[0]?.currency;

        const currDateText = formatDateRange(
          previousDates?.curr_start_date,
          previousDates?.curr_end_date,
        );
        const prevDateText = formatDateRange(
          previousDates?.prev_start_date,
          previousDates?.prev_end_date,
        );

        const customLegend = (
          <div className="w-full max-h-[340px] overflow-y-auto">
            <div className="w-full border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-[12px] font-semibold text-slate-700">
                    <th className="py-2.5 px-3">Project</th>
                    <th className="py-2.5 px-3 text-right">Current</th>
                    {hasPrevious && (
                      <th className="py-2.5 px-3 text-right">Previous</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-[13px]">
                  {chartSeriesData.map((d) => {
                    const isHidden = hiddenProjects.includes(d.id);
                    return (
                      <tr
                        key={d.id}
                        onClick={() => toggleProjectVisibility(d.id)}
                        className={`cursor-pointer transition-colors duration-150 ${
                          isHidden
                            ? "opacity-40 bg-slate-50/50"
                            : "hover:bg-slate-50/80"
                        }`}
                      >
                        <td className="py-2.5 px-3 whitespace-nowrap">
                          <div className="flex items-center gap-2.5">
                            <span
                              className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                                isHidden ? "opacity-40" : ""
                              }`}
                              style={{ backgroundColor: d.color }}
                            />
                            <span
                              className={`font-semibold ${
                                isHidden
                                  ? "line-through text-slate-400"
                                  : "text-slate-800"
                              }`}
                            >
                              {d.label}
                            </span>
                          </div>
                        </td>
                        <td
                          className={`py-2.5 px-3 text-right font-semibold tabular-nums whitespace-nowrap ${
                            isHidden ? "text-slate-400" : "text-slate-900"
                          }`}
                        >
                          {formatValue(d.currentValue, d.currency, metricKey)}
                        </td>
                        {hasPrevious && (
                          <td
                            className={`py-2.5 px-3 text-right font-semibold tabular-nums whitespace-nowrap ${
                              isHidden ? "text-slate-400" : "text-slate-600"
                            }`}
                          >
                            {formatValue(
                              d.previousValue,
                              d.currency,
                              metricKey,
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );

        return (
          <div className="flex flex-col lg:flex-row w-full h-full items-center gap-8 py-2">
            {/* Charts Section */}
            <div className="flex-1 flex flex-col md:flex-row w-full gap-6 justify-center items-center">
              {/* Current Donut */}
              <div className="flex-1 flex flex-col items-center w-full">
                {hasPrevious && (
                  <div className="flex flex-col items-center mb-2 text-center">
                    <div className="flex items-center gap-1 text-slate-800 font-bold text-sm">
                      <span>Current Period</span>
                      <svg
                        className="w-3.5 h-3.5 text-slate-400"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                    </div>
                    {currDateText && (
                      <span className="text-xs text-blue-600 font-medium mt-0.5">
                        {currDateText}
                      </span>
                    )}
                  </div>
                )}

                {hasMeaningfulCurr ? (
                  <div className="relative flex items-center justify-center">
                    <PieChart
                      margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                      series={[
                        {
                          data: visibleData.map((d) => ({
                            id: d.id,
                            value: d.currentValue,
                            label: d.label,
                            currency: d.currency,
                            color: d.color,
                          })),
                          innerRadius: 42,
                          outerRadius: 108,
                          paddingAngle: 0.5,
                          valueFormatter: safelyFormatPieValue,
                          highlightScope: {
                            faded: "global",
                            highlight: "item",
                          },
                          faded: {
                            innerRadius: 36,
                            additionalRadius: -8,
                            color: "gray",
                          },
                        },
                      ]}
                      slotProps={{ legend: { hidden: true } }}
                      height={240}
                      width={240}
                    />
                    {/* Total Value inside Donut Hole */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-base font-extrabold text-slate-900 tracking-tight leading-none">
                        {formatValue(totalCurrent, defaultCurrency, metricKey)}
                      </span>
                      <span className="text-[10px] font-semibold text-slate-500 mt-1 leading-none uppercase tracking-wide">
                        Total
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-slate-400 h-[240px] flex items-center italic">
                    No data
                  </div>
                )}
              </div>

              {/* Vertical Separator for Compare mode */}
              {hasPrevious && (
                <div className="hidden md:block w-px h-56 bg-slate-200/80" />
              )}

              {/* Previous Donut */}
              {hasPrevious && (
                <div className="flex-1 flex flex-col items-center w-full border-t md:border-t-0 border-slate-200 pt-4 md:pt-0">
                  <div className="flex flex-col items-center mb-2 text-center">
                    <div className="flex items-center gap-1 text-slate-800 font-bold text-sm">
                      <span>Previous Period</span>
                      <svg
                        className="w-3.5 h-3.5 text-slate-400"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                    </div>
                    {prevDateText && (
                      <span className="text-xs text-blue-600 font-medium mt-0.5">
                        {prevDateText}
                      </span>
                    )}
                  </div>

                  {hasMeaningfulPrev ? (
                    <div className="relative flex items-center justify-center">
                      <PieChart
                        margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                        series={[
                          {
                            data: visibleData.map((d) => ({
                              id: d.id,
                              value: d.previousValue,
                              label: d.label,
                              currency: d.currency,
                              color: d.color,
                            })),
                            innerRadius: 42,
                            outerRadius: 108,
                            paddingAngle: 0.5,
                            valueFormatter: safelyFormatPieValue,
                            highlightScope: {
                              faded: "global",
                              highlight: "item",
                            },
                            faded: {
                              innerRadius: 36,
                              additionalRadius: -8,
                              color: "gray",
                            },
                          },
                        ]}
                        slotProps={{ legend: { hidden: true } }}
                        height={240}
                        width={240}
                      />
                      {/* Total Value inside Donut Hole */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <span className="text-base font-extrabold text-slate-900 tracking-tight leading-none">
                          {formatValue(
                            totalPrevious,
                            defaultCurrency,
                            metricKey,
                          )}
                        </span>
                        <span className="text-[10px] font-semibold text-slate-500 mt-1 leading-none uppercase tracking-wide">
                          Total
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-400 h-[240px] flex items-center italic">
                      No data
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Table Legend */}
            <div className="w-full lg:w-auto min-w-[280px] lg:min-w-[340px] max-w-full lg:max-w-[45%] border-t lg:border-t-0 lg:border-l border-slate-200 pt-4 lg:pt-0 lg:pl-6">
              {customLegend}
            </div>
          </div>
        );
      }

      case "bar": {
        const baseData = chartDataMap[selectedMetrics[0]] || [];
        const xAxisConfig = [
          {
            scaleType: "band",
            data: baseData.map((item) => item.label),
            tickLabelStyle: { fill: "#94A3B8", fontSize: 11 },
          },
        ];

        const seriesData = [];
        selectedMetrics.forEach((m, index) => {
          if (hiddenMetrics.includes(m)) return;

          const metricTitle =
            ALL_METRICS.find((item) => item.key === m)?.title || m;
          const data = chartDataMap[m] || [];
          const hasPrevious = data.some((d) => d.previousValue !== null);
          const baseColor = getMetricColor(m, index);
          const isCost = m === "costs" || m === "cost";

          seriesData.push({
            id: m,
            data: data.map((item) => item.currentValue),
            label: hasPrevious ? `${metricTitle} (Current)` : metricTitle,
            valueFormatter: (v, context) => {
              const currency =
                context?.dataIndex !== undefined
                  ? data[context.dataIndex]?.currency
                  : data[0]?.currency;
              return formatValue(v, currency, m);
            },
            color: baseColor,
            yAxisId:
              isCost && selectedMetrics.length > 1 ? "rightAxis" : "leftAxis",
          });

          if (hasPrevious) {
            seriesData.push({
              id: `${m}_prev`,
              data: data.map((item) => item.previousValue),
              label: `${metricTitle} (Previous)`,
              valueFormatter: (v, context) => {
                const currency =
                  context?.dataIndex !== undefined
                    ? data[context.dataIndex]?.currency
                    : data[0]?.currency;
                return formatValue(v, currency, m);
              },
              color: `${baseColor}80`,
              yAxisId:
                isCost && selectedMetrics.length > 1 ? "rightAxis" : "leftAxis",
            });
          }
        });

        const hasCost =
          selectedMetrics.some((m) => m === "costs" || m === "cost") &&
          selectedMetrics.length > 1;

        const yAxisConfig = [
          {
            id: "leftAxis",
            position: "left",
            valueFormatter: formatYAxisValue,
            tickLabelStyle: { fill: "#94A3B8", fontSize: 11 },
          },
        ];

        if (hasCost) {
          yAxisConfig.push({
            id: "rightAxis",
            position: "right",
            valueFormatter: formatYAxisValue,
            tickLabelStyle: { fill: "#94A3B8", fontSize: 11 },
          });
        }

        return (
          <BarChart
            xAxis={xAxisConfig}
            yAxis={yAxisConfig}
            series={seriesData}
            height={dynamicHeightLineBar}
            margin={{
              left: 50,
              right: hasCost ? 50 : 20,
              top: 20,
              bottom: 40,
            }}
            grid={{ horizontal: true }}
            slotProps={{ legend: { hidden: true } }}
          />
        );
      }

      case "line": {
        const baseData = chartDataMap[selectedMetrics[0]] || [];
        const xAxisConfig = [
          {
            scaleType: "point",
            data: baseData.map((item) => item.label),
            tickLabelStyle: { fill: "#94A3B8", fontSize: 11 },
          },
        ];

        const seriesData = [];
        selectedMetrics.forEach((m, index) => {
          if (hiddenMetrics.includes(m)) return;

          const metricTitle =
            ALL_METRICS.find((item) => item.key === m)?.title || m;
          const data = chartDataMap[m] || [];
          const hasPrevious = data.some((d) => d.previousValue !== null);
          const baseColor = getMetricColor(m, index);
          const isCost = m === "costs" || m === "cost";

          seriesData.push({
            id: m,
            data: data.map((item) => item.currentValue),
            label: hasPrevious ? `${metricTitle} (Current)` : metricTitle,
            valueFormatter: (v, context) => {
              const currency =
                context?.dataIndex !== undefined
                  ? data[context.dataIndex]?.currency
                  : data[0]?.currency;
              return formatValue(v, currency, m);
            },
            color: baseColor,
            yAxisId:
              isCost && selectedMetrics.length > 1 ? "rightAxis" : "leftAxis",
            showMark: true,
            area: true,
            curve: "catmullRom",
          });

          if (hasPrevious) {
            seriesData.push({
              id: `${m}_prev`,
              data: data.map((item) => item.previousValue),
              label: `${metricTitle} (Previous)`,
              valueFormatter: (v, context) => {
                const currency =
                  context?.dataIndex !== undefined
                    ? data[context.dataIndex]?.currency
                    : data[0]?.currency;
                return formatValue(v, currency, m);
              },
              color: `${baseColor}80`,
              yAxisId:
                isCost && selectedMetrics.length > 1 ? "rightAxis" : "leftAxis",
              showMark: true,
              area: false,
              curve: "catmullRom",
            });
          }
        });

        const hasCost =
          selectedMetrics.some((m) => m === "costs" || m === "cost") &&
          selectedMetrics.length > 1;

        const yAxisConfig = [
          {
            id: "leftAxis",
            position: "left",
            valueFormatter: formatYAxisValue,
            tickLabelStyle: { fill: "#94A3B8", fontSize: 11 },
          },
        ];

        if (hasCost) {
          yAxisConfig.push({
            id: "rightAxis",
            position: "right",
            valueFormatter: formatYAxisValue,
            tickLabelStyle: { fill: "#94A3B8", fontSize: 11 },
          });
        }

        const gradientDefs = selectedMetrics.map((m) => {
          const colorInfo = METRIC_COLORS[m] || {
            border: getMetricColor(m),
          };
          return (
            <linearGradient
              id={`area-gradient-${m}`}
              key={m}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor={colorInfo.border}
                stopOpacity="0.35"
              />
              <stop
                offset="100%"
                stopColor={colorInfo.border}
                stopOpacity="0.0"
              />
            </linearGradient>
          );
        });

        const sxRules = {
          ...selectedMetrics.reduce((acc, m, idx) => {
            const color = getMetricColor(m, idx);
            acc[`& .MuiAreaElement-series-${m}`] = {
              fill: `url(#area-gradient-${m}) !important`,
            };
            acc[`& .MuiMarkElement-series-${m}`] = {
              fill: `${color} !important`,
              stroke: "#ffffff !important",
              strokeWidth: 2,
              r: 4,
            };
            return acc;
          }, {}),
          "& .MuiChartsGrid-line": {
            strokeDasharray: "4 4",
            stroke: "#F1F5F9",
          },
        };

        return (
          <LineChart
            xAxis={xAxisConfig}
            yAxis={yAxisConfig}
            series={seriesData}
            height={dynamicHeightLineBar}
            margin={{
              left: 50,
              right: hasCost ? 50 : 20,
              top: 20,
              bottom: 40,
            }}
            grid={{ horizontal: true }}
            slotProps={{ legend: { hidden: true } }}
            sx={sxRules}
          >
            <defs>{gradientDefs}</defs>
          </LineChart>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div
      ref={chartWrapperRef}
      className={`bg-white transition-all duration-300 ease-in-out flex flex-col border border-slate-100 ${isFullScreen ? "fixed inset-0 z-50 p-4 sm:p-6 md:p-8 overflow-y-auto w-screen h-screen rounded-none shadow-xl" : "rounded-2xl shadow-sm hover:shadow-md w-full"}`}
    >
      <div
        className={`grid transition-all duration-300 ease-in-out flex-1 ${isExpanded ? `grid-rows-[1fr] opacity-100 ${!isFullScreen ? "border-b border-slate-100" : ""}` : "grid-rows-[0fr] opacity-0"}`}
      >
        <div className="overflow-hidden flex flex-col h-full">
          <div className="p-4 sm:p-6 flex flex-col flex-1">
            {/* Header Controls Row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4">
              <div className="text-slate-800 font-semibold hidden sm:block">
                {isFullScreen && "Performance Overview"}
              </div>

              <div className="flex flex-wrap gap-2.5 items-center justify-end w-full sm:w-auto">
                {/* Download Button */}
                <div className="relative" ref={chartDownloadRef}>
                  <button
                    onClick={() => setShowChartDownloadMenu((prev) => !prev)}
                    disabled={isChartDownloading}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-200 border text-xs font-medium focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                      showChartDownloadMenu
                        ? "bg-blue-50 text-blue-600 border-blue-200"
                        : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                    }`}
                    title="Download Chart"
                  >
                    {isChartDownloading ? (
                      <>
                        <svg
                          className="animate-spin h-3.5 w-3.5 text-blue-600 shrink-0"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>Downloading...</span>
                      </>
                    ) : (
                      <>
                        <MdOutlineFileDownload className="text-base shrink-0 text-slate-500" />
                        <span className="hidden sm:inline">Download</span>
                      </>
                    )}
                  </button>

                  {showChartDownloadMenu && (
                    <div className="absolute right-0 top-full mt-2 w-32 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-1 origin-top-right text-xs">
                      <div
                        className="px-3 py-2 hover:bg-slate-50 cursor-pointer rounded-lg text-slate-700 font-medium"
                        onClick={() => handleChartDownload("pdf")}
                      >
                        .PDF
                      </div>
                      <div
                        className="px-3 py-2 hover:bg-slate-50 cursor-pointer rounded-lg text-slate-700 font-medium"
                        onClick={() => handleChartDownload("png")}
                      >
                        .PNG
                      </div>
                    </div>
                  )}
                </div>

                {/* Expand / Collapse Button */}
                <button
                  onClick={toggleFullScreen}
                  className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none transition-all cursor-pointer"
                  title={isFullScreen ? "Collapse Chart" : "Expand Chart"}
                >
                  {isFullScreen ? (
                    <FaCompress className="text-xs text-slate-500" />
                  ) : (
                    <FaExpand className="text-xs text-slate-500" />
                  )}
                  <span className="hidden sm:inline">
                    {isFullScreen ? "Collapse" : "Expand"}
                  </span>
                </button>

                {/* Chart Type Dropdown */}
                <div className="relative">
                  <select
                    value={chartType}
                    onChange={handleChartTypeChange}
                    className="appearance-none bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-700 pl-3 pr-8 py-1.5 hover:bg-slate-50 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="line">Line Chart</option>
                    <option value="bar">Bar Chart</option>
                    <option value="pie">Pie Chart</option>
                  </select>
                  <svg
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5 pointer-events-none"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>

                {/* 3-Dots Settings Trigger Button */}
                <div className="flex items-center relative" ref={adjustMenuRef}>
                  <button
                    onClick={() => setIsAdjustOpen(!isAdjustOpen)}
                    className="p-1.5 border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-all focus:outline-none"
                    aria-label="Modify Metrics Settings"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                      />
                    </svg>
                  </button>

                  {/* Metrics Settings Popup */}
                  {isAdjustOpen && (
                    <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-4 origin-top-right">
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 pb-2 border-b border-slate-100">
                        Metrics Settings
                      </h3>
                      <div className="flex flex-col gap-2">
                        <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                          Select Metrics ({selectedMetrics.length}/
                          {selectionLimit})
                        </label>
                        <button
                          onClick={() => {
                            setIsMetricsModalOpen(true);
                            setIsAdjustOpen(false);
                          }}
                          className="w-full text-left bg-white border border-slate-200 text-xs font-medium text-slate-700 rounded-lg hover:bg-slate-50 p-2.5 transition-all flex justify-between items-center"
                        >
                          <span className="truncate mr-2 block">
                            {selectedMetrics
                              .map(
                                (m) =>
                                  ALL_METRICS.find((x) => x.key === m)?.title,
                              )
                              .join(", ") || "Select metrics..."}
                          </span>
                          <svg
                            className="text-slate-400 shrink-0"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Top Left Metric Legend Placement */}
            {chartType !== "pie" && (
              <div className="flex flex-wrap items-center gap-6 mb-3">
                {selectedMetrics.map((m, index) => {
                  const metricInfo = ALL_METRICS.find((item) => item.key === m);
                  const title = metricInfo?.title || m;
                  const isHidden = hiddenMetrics.includes(m);
                  const color = getMetricColor(m, index);

                  return (
                    <div
                      key={m}
                      onClick={() =>
                        setHiddenMetrics((prev) =>
                          prev.includes(m)
                            ? prev.filter((x) => x !== m)
                            : [...prev, m],
                        )
                      }
                      className="flex items-center gap-2 cursor-pointer group"
                    >
                      <span
                        className={`w-2.5 h-2.5 rounded-full inline-block transition-all duration-200 ${
                          isHidden ? "opacity-30 scale-75" : "shadow-sm"
                        }`}
                        style={{ backgroundColor: color }}
                      />
                      <span
                        className={`text-xs font-medium transition-all duration-200 ${
                          isHidden
                            ? "line-through text-slate-400 font-normal"
                            : "text-slate-600 group-hover:text-slate-900"
                        }`}
                      >
                        {title}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Main Chart Canvas Area */}
            <div
              className={`flex justify-center items-center w-full flex-1 min-h-[250px] relative ${isFullScreen ? "mt-2" : ""}`}
            >
              {loading ? (
                <div className="text-slate-400 text-xs font-medium animate-pulse">
                  Loading chart...
                </div>
              ) : error ? (
                <p className="text-red-500 text-xs font-medium">{error}</p>
              ) : (
                renderChart()
              )}
            </div>
          </div>
        </div>
      </div>

      {!isFullScreen && (
        <div
          className="flex items-center justify-between px-4 sm:px-6 py-3 cursor-pointer select-none group bg-white hover:bg-slate-50 transition-colors rounded-b-2xl mt-auto"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg group-hover:bg-blue-100 transition-colors">
              <FaChartArea size={16} />
            </div>
            <h2 className="text-base sm:text-lg font-semibold text-slate-800 tracking-tight">
              Performance Overview
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-slate-400 group-hover:text-slate-700 bg-slate-100 p-2 rounded-md transition-colors">
              {isExpanded ? (
                <FaChevronUp size={14} />
              ) : (
                <FaChevronDown size={14} />
              )}
            </div>
          </div>
        </div>
      )}

      <MetricsModal
        isOpen={isMetricsModalOpen}
        onClose={() => setIsMetricsModalOpen(false)}
        allMetrics={ALL_METRICS}
        initialSelectedMetrics={selectedMetrics}
        onApply={handleApplyMetrics}
        selectionLimit={selectionLimit}
      />
    </div>
  );
};

export default PaidAdsChart;
